import styled from '@emotion/styled'

import { capitalizeFirstLetter } from '@livechat/data-utils'

import RuleConditionTooltip from './RuleConditionTooltip'
import { RuleCondition } from 'types/rules'

const EXTRACTOR_DISPLAY_MAP: Record<RuleCondition['extractor'], string> = {
	url: 'URL',
	domain: 'Domain',
}

const ConditionGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space2};
	width: 100%;
`

const ConditionExtractor = styled.span`
	${({ theme }) => theme.typography.captionSmall};
	color: ${({ theme }) => theme.colors.light.grayscale[500]};
	padding-bottom: ${({ theme }) => theme.spaces.space2};
`

const ConditionText = styled.span`
	${({ theme }) => theme.typography.captionSmall};
	color: ${({ theme }) => theme.colors.light.grayscale[500]};
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
	max-width: 100%;
`

const PatternText = styled.span`
	${({ theme }) => theme.typography.smallcaps};
	color: ${({ theme }) => theme.colors.light.primaryTextColor};
`

const ConditionCounter = styled.div`
	display: inline-flex;
	align-items: center;
	padding: ${({ theme }) => `${theme.spaces.space2} 6px`};
	background-color: ${({ theme }) => theme.colors.light.surfaceDecorative};
	border-radius: ${({ theme }) => theme.borderRadiuses.xxl};
	${({ theme }) => theme.typography.mini};
	color: ${({ theme }) => theme.colors.light.secondaryTextColor};
	align-self: flex-start;
`

type RuleConditionGroupProps = {
	conditions: RuleCondition[]
	maxVisible: number
}

const RuleConditionGroup = ({ conditions, maxVisible }: RuleConditionGroupProps) => {
	if (conditions.length === 0) {
		return null
	}

	const visibleConditions = conditions.slice(0, maxVisible)
	const remainingConditions = conditions.slice(maxVisible)
	const remainingCount = conditions.length - maxVisible

	return (
		<ConditionGroup>
			<ConditionExtractor>{EXTRACTOR_DISPLAY_MAP[conditions[0].extractor]}</ConditionExtractor>

			{visibleConditions.map((condition) => (
				<ConditionText key={condition.id}>
					{capitalizeFirstLetter(condition.matcher)} <PatternText>{condition.pattern}</PatternText>
				</ConditionText>
			))}

			{remainingCount > 0 && (
				<RuleConditionTooltip remainingConditions={remainingConditions}>
					<ConditionCounter>+{remainingCount}</ConditionCounter>
				</RuleConditionTooltip>
			)}
		</ConditionGroup>
	)
}

export default RuleConditionGroup
